/**
 * DashboardCfePaidController — bouton « J'ai payé » de l'alerte CFE
 * catégorie 4 du dashboard payant §15.B (EPIC-032 / 32.5).
 *
 * Cohabite sur la racine `dashboard-payant-template` avec le controller
 * `dashboard-payant`. Écoute les clics `click->dashboard-cfe-paid#open`
 * sur les boutons "J'ai payé" injectés par le rendu d'alerte (catégorie 4).
 *
 * - `open(event)`    : clone le template `tpl-cfe-paid-modal-confirm`,
 *                     l'attache au DOM et appelle `showModal()` natif.
 * - `close()`        : ferme le dialog et le retire du DOM.
 * - `confirm()`      : PATCH /me/cfe-status, ferme le dialog, émet
 *                     l'event Stimulus `dashboard-cfe-paid:success` que
 *                     `dashboard-payant` peut intercepter pour refetch.
 *
 * Distinct de `cfe-mark-paid` (page /cfe.html, EPIC-024.5) qui utilise
 * POST /cfe/mark-paid avec Idempotency-Key et retourne l'overview
 * complet. Ici PATCH idempotent côté serveur, pas d'header.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class DashboardCfePaidController extends BaseController {
  open(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const year = Number.parseInt(trigger.getAttribute("data-year") || "", 10)
      || new Date().getFullYear();

    const fragment = this.cloneTemplate("tpl-cfe-paid-modal-confirm");
    this.fillSlots(fragment, { "cfe-year": String(year) });

    const dialog = fragment.querySelector("dialog");
    if (dialog === null) return;
    dialog.dataset.year = String(year);
    document.body.appendChild(fragment);

    this._dialog = dialog;
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    }
  }

  close() {
    if (!this._dialog) return;
    if (typeof this._dialog.close === "function") {
      this._dialog.close();
    }
    this._dialog.remove();
    this._dialog = null;
  }

  async confirm() {
    if (!this._dialog) return;

    const year = Number.parseInt(this._dialog.dataset.year || "", 10)
      || new Date().getFullYear();
    const confirmBtn = this._dialog.querySelector("[data-action*='confirm']");
    const cancelBtn = this._dialog.querySelector("[data-action*='close']");
    const errorEl = this._dialog.querySelector(".nm-form__error");

    if (confirmBtn) confirmBtn.disabled = true;
    if (cancelBtn) cancelBtn.disabled = true;
    if (errorEl) {
      errorEl.textContent = "";
      errorEl.hidden = true;
    }

    try {
      const payload = await api.updateCfeStatus(year);
      this.close();
      this.showSuccess(`CFE ${year} marquée comme payée.`);
      this.dispatch("success", { detail: { cfePaidForYear: payload.cfePaidForYear } });
    } catch (error) {
      this._showInlineError(errorEl, error);
      if (confirmBtn) confirmBtn.disabled = false;
      if (cancelBtn) cancelBtn.disabled = false;
    }
  }

  _showInlineError(errorEl, error) {
    if (!errorEl) return;
    const status = error && typeof error === "object" ? error.status : null;
    let message = "Erreur lors de l'enregistrement. Réessayez plus tard.";
    if (status === 422 && error.details && Array.isArray(error.details.fields)) {
      const first = error.details.fields[0];
      if (first && typeof first.message === "string" && first.message !== "") {
        message = first.message;
      }
    } else if (error && typeof error.message === "string" && error.message !== "") {
      message = error.message;
    }
    errorEl.textContent = message;
    errorEl.hidden = false;
  }
}
